import { CoralogixRum } from '@coralogix/browser';

import { Logger } from 'src/services/Logger';
import { buildObservabilityUserTenantContext } from 'src/services/ObservabilityContextHelper';
import { redactContext } from 'src/services/ObservabilityRedactionHelper';
import { getUserMetadata } from 'src/services/UserService';

import { getUserTenant } from '../TenantsService';

type TCoralogixRumServiceConfig = {
	application: string;
	environment: string;
	publicKey: string | undefined;
	version: string;
};

class CoralogixRumService {
	private initialized = false;

	private readonly config: TCoralogixRumServiceConfig;

	constructor(config: TCoralogixRumServiceConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		try {
			if (this.initialized) {
				return;
			}

			if (!this.config.publicKey) {
				Logger.warn('Coralogix RUM: Public key not configured, skipping initialization');
				return;
			}

			const userInfo = await getUserMetadata();
			const tenantInfo = await getUserTenant();
			const userContext = buildObservabilityUserTenantContext(userInfo, tenantInfo);

			CoralogixRum.init({
				public_key: this.config.publicKey,
				application: this.config.application,
				version: this.config.version,
				environment: this.config.environment,
				coralogixDomain: 'US2',
				user_context: {
					user_id: userContext.userId,
					user_name: userContext.userName,
					user_email: userContext.userEmail,
					user_metadata: userContext.userMetadata
				},
				sessionSampleRate: this.config.environment === 'production' ? 100 : 0,
				instrumentations: {
					errors: true,
					interactions: true,
					long_tasks: true,
					fetch: true,
					xhr: true,
					resources: true,
					web_vitals: true
				},
				sessionRecordingConfig: {
					enable: this.config.environment === 'production',
					autoStartSessionRecording: true,
					recordConsoleEvents: true,
					sessionRecordingSampleRate: 100
				}
			});

			this.initialized = true;

			Logger.info('Coralogix RUM initialized successfully', {
				application: this.config.application,
				version: this.config.version,
				environment: this.config.environment
			});
		} catch (err) {
			Logger.error('Error initializing Coralogix RUM', err);
		}
	}

	captureError(error: Error, context?: Record<string, unknown>): void {
		try {
			CoralogixRum.captureError(error, redactContext(context));
		} catch (err) {
			Logger.error('Error logging to Coralogix RUM', err);
		}
	}

	addTiming(name: string, duration?: number): void {
		try {
			CoralogixRum.addTiming(name, duration);
		} catch (err) {
			Logger.error('Error adding custom timing to Coralogix RUM', err);
		}
	}

	sendCustomMeasurement(name: string, value: number): void {
		try {
			CoralogixRum.sendCustomMeasurement(name, value);
		} catch (err) {
			Logger.error('Error sending custom measurement to Coralogix RUM', err);
		}
	}
}

const coralogixRumService = new CoralogixRumService({
	application: 'product-hub',
	environment: import.meta.env.VITE_RUN_ENV || 'development',
	publicKey: import.meta.env.VITE_CORALOGIX_RUM_PUBLIC_KEY,
	// eslint-disable-next-line no-undef
	version: UI_VERSION || 'unknown'
});

/**
 * Initializing Coralogix RUM service for error tracking, performance monitoring, and bug detection
 */
export const initCoralogixRum = async (): Promise<void> => {
	await coralogixRumService.init();
};
